import { useState } from 'react';
import { ELEMENT_CATEGORIES, type ElementCategory, type IdentityCandidate } from '@scoutaround/script-breakdown-shared';

import { useModalFocus } from '../../lib/useModalFocus.js';
import { CategorySwatch } from './CategoryIcon.js';

/**
 * IS THIS THE SAME SWORD? — asked before a hand-added name is joined to something the production
 * already has (owner safeguard S1).
 *
 * Two swords are not one sword because both are called "sword". When the name matches an item
 * this scene never read, the server refuses to merge it silently and names the candidates; the
 * producer says which: the same item (it joins it, with its scenes) or a different one, which is
 * added separately — best under a name that tells the two apart.
 */
export function IdentityDialog({
  name,
  category,
  candidates,
  suggestion,
  busy,
  error,
  onSame,
  onDistinct,
  onCancel,
}: {
  name: string;
  category: ElementCategory;
  candidates: readonly IdentityCandidate[];
  /** A telling name to offer for a different item ("Sam’s sword"), when there is one. */
  suggestion: string | null;
  busy: boolean;
  error: string | null;
  onSame: (productionElementId: string) => void;
  onDistinct: (name: string) => void;
  onCancel: () => void;
}) {
  const modal = useModalFocus<HTMLDivElement>(() => {
    if (!busy) onCancel();
  });
  const [distinctName, setDistinctName] = useState(name);
  const first = candidates[0];
  const scenesOf = (c: IdentityCandidate) =>
    c.sceneNumbers.length === 0
      ? 'in no scene yet'
      : `in ${c.sceneNumbers.length === 1 ? 'scene' : 'scenes'} ${c.sceneNumbers.join(', ')}`;
  const question =
    candidates.length === 1 && first !== undefined
      ? `Is this the same ${first.name} as ${scenesOf(first)}?`
      : `Is this one of the items already called “${name}”?`;

  return (
    <div
      className="el-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="el-identity-title"
      data-testid="el-identity-dialog"
      onKeyDown={modal.onKeyDown}
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="el-modal-box" ref={modal.ref}>
        <header className="el-modal-head">
          <p className="el-modal-kicker">
            <CategorySwatch category={category} />
            {ELEMENT_CATEGORIES[category].label}
          </p>
          <h2 className="el-modal-title" id="el-identity-title" data-testid="el-identity-question">
            {question}
          </h2>
        </header>

        <div className="el-modal-body">
          <p className="el-modal-text">
            Something with this name is already in the production, but this scene was never read for it. Joining them makes
            one item across their scenes; a different item stays separate.
          </p>

          <ul className="el-identity-candidates">
            {candidates.map((candidate) => (
              <li key={candidate.productionElementId} data-testid={`el-identity-candidate-${candidate.productionElementId}`}>
                <span>
                  <strong>{candidate.name}</strong>
                  <span className="el-identity-meta">
                    {`${ELEMENT_CATEGORIES[candidate.category].label} · ${scenesOf(candidate)}`}
                  </span>
                </span>
                <button
                  type="button"
                  className="ctl"
                  disabled={busy}
                  data-testid={`el-identity-same-${candidate.productionElementId}`}
                  onClick={() => {
                    onSame(candidate.productionElementId);
                  }}
                >
                  Same item
                </button>
              </li>
            ))}
          </ul>

          <form
            className="el-identity-distinct"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = distinctName.trim();
              if (trimmed !== '') onDistinct(trimmed);
            }}
          >
            <label htmlFor="el-identity-name">A different item, named</label>
            <div className="el-identity-distinct-row">
              <input
                id="el-identity-name"
                value={distinctName}
                maxLength={255}
                data-testid="el-identity-name"
                onChange={(event) => {
                  setDistinctName(event.target.value);
                }}
              />
              <button
                type="submit"
                className="ctl"
                disabled={busy || distinctName.trim() === ''}
                data-testid="el-identity-distinct"
              >
                A different item
              </button>
            </div>
            <p className="el-modal-hint" data-testid="el-identity-hint">
              {suggestion === null
                ? 'A name that tells them apart helps everyone later, such as whose it is.'
                : `A name that tells them apart helps everyone later, such as “${suggestion}”.`}
            </p>
          </form>

          {error === null ? null : (
            <p className="el-modal-error" role="alert" data-testid="el-identity-error">
              {error}
            </p>
          )}
        </div>

        <footer className="el-modal-foot">
          <button type="button" className="btn btn-ghost" disabled={busy} data-testid="el-identity-cancel" onClick={onCancel}>
            Cancel
          </button>
        </footer>
      </div>
    </div>
  );
}
